<template>
  <div id="application-rules__hosts">
    <div class="subheading font-weight-bold">
      {{ $t("modules.dataPath.hosts") }}
    </div>
    <v-row>
      <v-col>
        <v-select
          v-model="hostRedundancyLevel"
          :items="redundancyLevels"
          :label="$t('modules.dataPath.redundancyLevel')"
          id="application-rules__form-redundancy-level-hosts"
          :rules="[
            v =>
              (v !== null && v !== undefined) ||
              $t('validation.required', [
                $t('modules.dataPath.redundancyLevel')
              ])
          ]"
          required
        >
          <template v-slot:prepend>
            <v-tooltip color="info" top max-width="400">
              <template v-slot:activator="{ on }">
                <v-icon v-on="on">mdi-information</v-icon>
              </template>
              <div v-html="$t('modules.dataPath.redundancyInfo')"></div>
            </v-tooltip>
          </template>
        </v-select>
      </v-col>
      <v-col v-if="hostRedundancyLevel === 4">
        <v-text-field
          v-model="hostRedundancyPercentage"
          type="number"
          step="1"
          :label="$t('modules.dataPath.redundancyPercentage')"
          :hint="$t('modules.dataPath.redundancyPercentageHint')"
          id="application-rules__form-redundancy-percentage-hosts"
          :rules="redundancyPercentageRules"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-slider
      v-model="slider"
      class="align-center"
      :max="max"
      :min="0"
      :label="$t('modules.dataPath.reorder') + ' ' + $t('modules.dataPath.ms')"
      thumb-label
      :rules="reorderRules"
      color="primary"
      thumb-color="primary"
    >
      <template v-slot:append>
        <v-text-field
          v-model="sliderInput"
          class="mt-0 pt-0"
          single-line
          type="number"
          :max="max"
          style="width: 60px"
        ></v-text-field>
      </template>
    </v-slider>
    <v-btn color="primary" @click="openEditDialog()" :disabled="isLoading">
      {{ $t("modules.dataPath.editHosts") }}
    </v-btn>
    <v-spacer></v-spacer>
    <div class="text-xs-right" id="application-rules__total-hosts">
      Total: {{ hostsList.length }}
    </div>

    <v-dialog v-model="hostDialog" width="600" persistent>
      <v-card>
        <v-card-title class="headline my-0" primary-title>
          {{ $t("modules.dataPath.editHosts") }}
          <v-spacer></v-spacer>
          <span class="caption"
            >Total: {{ devices.length }}, Selected: {{ selected.length }}</span
          >
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text>
          <v-row dense>
            <v-col cols="6">
              <v-text-field
                append-icon="mdi-magnify"
                :label="$t('actions.search')"
                single-line
                hide-details
                v-model="search"
                autofocus
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select
                append-icon="mdi-filter-variant"
                :label="$t('actions.filter')"
                single-line
                hide-details
                v-model="filterSelected"
                :items="filterSelectedItems"
                autofocus
                clearable
              ></v-select>
            </v-col>
          </v-row>
          <v-data-table
            :headers="editHeaders"
            :items="filteredItems"
            :loading="isLoading"
            v-model="selected"
            hide-default-footer
            disable-pagination
            show-select
            @click:row="rowClick"
            class="elevation-1"
            style="max-height: 400px;overflow: auto"
          >
          </v-data-table>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text elevation="1" @click="cancel()">
            {{ $t("actions.cancel") }}
          </v-btn>
          <v-btn
            color="secondary"
            text
            elevation="1"
            @click="save()"
            :disabled="isLoading"
          >
            {{ $t("actions.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-data-table
      :headers="headers"
      :items="hostsList"
      :loading="isLoading"
      class="elevation-1"
      style="max-height: 200px;overflow: auto"
      disable-pagination
      hide-default-footer
    >
      <template v-slot:items="props">
        <td>{{ props.item.name }}</td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { UnauthorizedError, handleError } from "@/utils/error";
import { permissionMixin } from "@/mixins/permissions";

export default {
  name: "ApplicationRuleHosts",
  mixins: [permissionMixin],
  data() {
    return {
      isLoading: false,
      search: "",
      slider: 0,
      sliderInput: 0,
      filteredItems: [],
      filterSelected: null,
      minRedundancyPercentage: 0,
      maxRedundancyPercentage: 1000,
      max: 1000,
      selected: [],
      filterSelectedItems: [
        {
          text: this.$t("modules.dataPath.assigned"),
          value: true
        },
        {
          text: this.$t("modules.dataPath.nonAssigned"),
          value: false
        }
      ],
      editHeaders: [
        { text: "", value: "select", sortable: false },
        { text: this.$t("modules.dataPath.hostName"), value: "name" },
        {
          text: this.$t("modules.dataPath.serialNumber"),
          value: "pairing_id"
        }
      ],
      headers: [
        {
          text: this.$t("modules.dataPath.hostName"),
          value: "name",
          align: "text-xs-center"
        }
      ],
      redundancyLevels: [
        { value: 0, text: this.$t("modules.dataPath.none") },
        { value: 1, text: this.$t("modules.dataPath.double") },
        { value: 2, text: this.$t("modules.dataPath.triple") },
        { value: 3, text: this.$t("modules.dataPath.quadruple") },
        { value: 4, text: this.$t("modules.dataPath.custom") }
      ],
      hostDialog: false,
      hostsList: [],
      redundancyPercentageRules: [
        v =>
          !!v ||
          this.$t("validation.required", [
            this.$t("modules.dataPath.redundancyPercentage")
          ]),
        v =>
          (Number.isInteger(Number(v)) &&
            v >= this.minRedundancyPercentage &&
            v <= this.maxRedundancyPercentage) ||
          this.$t("validation.invalid", [
            this.$t("modules.dataPath.redundancyPercentage")
          ])
      ],
      reorderRules: [
        v => {
          if (
            this.sliderInput > this.max ||
            this.sliderInput < 0 ||
            (v > 0 && v < 5)
          ) {
            return this.$t("modules.dataPath.reorderRange", [
              "5",
              this.max.toString()
            ]);
          } else {
            return true;
          }
        }
      ]
    };
  },

  watch: {
    search() {
      this.filterItems();
    },

    filterSelected() {
      this.filterItems();
    },

    slider(val) {
      if (val < 0) {
        this.hostReorder = 0;
        this.sliderInput = 0;
      } else if (val > this.max) {
        this.hostReorder = this.max;
      } else {
        this.hostReorder = val;
        this.sliderInput = val;
      }
    },

    sliderInput(val) {
      if (val < 0) {
        this.slider = 0;
      } else if (val > this.max) {
        this.slider = this.max;
      } else {
        this.slider = val;
      }
    }
  },

  computed: {
    devices() {
      let devices = this.$store.state.devices.devices;
      if (this.isPermitted(this.PermissionEnum.DEVICES.VIEW)) {
        return devices.filter(
          item =>
            item.pairing_id.indexOf("server") === -1 &&
            item.id !== this.primaryHub &&
            item.id !== this.secondaryHub &&
            item.id !== this.tertiaryHub
        );
      }
      return [];
    },

    hostRedundancyLevel: {
      get() {
        return this.$store.state.dataPath.editedItem.hostRedundancyLevel;
      },
      set(val) {
        this.$store.commit("dataPath/updateHostRedundancyLevel", val);
      }
    },

    hostRedundancyPercentage: {
      get() {
        return this.$store.state.dataPath.editedItem.hostRedundancyPercentage;
      },
      set(val) {
        this.$store.commit("dataPath/updateHostRedundancyPercentage", val);
      }
    },

    hostReorder: {
      get() {
        return this.$store.state.dataPath.editedItem.hostReorder;
      },
      set(val) {
        this.$store.commit("dataPath/updateHostReorder", val);
      }
    },

    hosts() {
      return this.$store.state.dataPath.editedItem.hosts;
    },

    ...mapState("applicationRules", {
      primaryHub: state => state.editedItem.primaryHub,
      secondaryHub: state => state.editedItem.secondaryHub,
      tertiaryHub: state => state.editedItem.tertiaryHub
    }),

    primaryHub: {
      get() {
        return this.$store.state.dataPath.editedItem.primaryHub;
      },
      set(val) {
        this.$store.commit("dataPath/updatePrimaryHub", val);
      }
    },

    secondaryHub: {
      get() {
        return this.$store.state.dataPath.editedItem.secondaryHub;
      },
      set(val) {
        this.$store.commit("dataPath/updateSecondaryHub", val);
      }
    },

    tertiaryHub: {
      get() {
        return this.$store.state.dataPath.editedItem.tertiaryHub;
      },
      set(val) {
        this.$store.commit("dataPath/updateTertiaryHub", val);
      }
    }
  },

  created() {
    this.isLoading = true;
    this.slider = this.hostReorder;
    this.$store
      .dispatch("devices/fetchAll")
      .catch(error => handleError(error))
      .then(() => {
        this.filteredItems = this.devices;
        this.hostsList = this.devices.filter(data => {
          return this.hosts.indexOf(data.id) !== -1;
        });
      })
      .catch(error => {
        if (error instanceof UnauthorizedError) {
          this.$store.dispatch("dataPath/toggleDialog", false);
        }

        handleError(error);
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    openEditDialog() {
      this.hostDialog = true;
      this.selected = this.hostsList;
    },

    rowClick(item, row) {
      let selectState = !row.isSelected;
      row.select(selectState);
    },

    filterItems() {
      this.filteredItems = this.devices
        .filter(item => {
          if (this.search) {
            return (
              item.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1 ||
              item.pairing_id.toLowerCase().indexOf(this.search.toLowerCase()) >
                -1
            );
          }
          return true;
        })
        .filter(item => {
          if (this.filterSelected === true) {
            return this.selected.indexOf(item) > -1;
          } else if (this.filterSelected === false) {
            return this.selected.indexOf(item) === -1;
          }
          return true;
        });
    },

    save() {
      this.hostsList = this.selected;
      let hosts = this.hostsList.map(data => {
        return data.id;
      });
      this.$store.commit("dataPath/updateHosts", hosts);
      this.hostDialog = false;
      this.filterSelected = null;
      this.search = "";
    },

    cancel() {
      this.hostDialog = false;
      this.filterSelected = null;
      this.search = "";
    }
  }
};
</script>
